import moment from "moment";
import { formattedTableDateString } from "../../helpers";
import { isNumber } from "util";

export default (state: any, action: any) => {
  switch (action.type) {
    case "SET_EMPLOYEES":
      const result = paginateEmployees(
        state.pagination.currentPage,
        state.pagination.items,
        action.payload.employees
      );
      return {
        ...state,
        employees: result.employees,
        employeesPaginated: result.employees,
        employeesCache: action.payload.employees,
        pagination: {
          ...state.pagination,
          totalPages: result.totalPages,
        },
      };
    case "SET_CHARTS_LABELS":
      return {
        ...state,
        chartLabels: action.payload.labels,
      };
    case "SET_TASKS":
      return {
        ...state,
        tasks: action.payload.tasks,
      }
    case "SET_BUILDINGS":
      return {
        ...state,
        buildings: action.payload.buildings,
      }
    case "SET_RATE":
      return {
        ...state,
        rate: action.payload.rate,
      }
    case "SET_REVIEWS":
      return {
        ...state,
        reviews: action.payload.reviews,
      }
    case "SET_WORKING_HOURS":
      return {
        ...state,
        workingHours: action.payload.workingHours,
      }
    case "PAGINATE_EMPLOYEES":
      const { items, currentPage } = action.payload.params;
      const { employees, totalPages } = paginateEmployees(
        currentPage,
        items,
        state.employeesCache
      );
      return {
        ...state,
        employees,
        employeesPaginated: employees,
        pagination: {
          ...state.pagination,
          currentPage: action.payload.params.currentPage,
          items: action.payload.params.items,
          totalPages,
        },
      };
    case "SET_CHARTS_FILTER":
      return {
        ...state,
        activeFilter: action.payload.activeFilter,
        activeRange: getFilterRange(state, action),
      };
    case "SET_FILTERS":
      return {
        ...state,
        filters: [...action.payload.filters],
      };
    case "SET_CURRENT_EMPLOYEE":
      return {
        ...state,
        currentEmployee: action.payload.employee,
      };
    case "SET_ACTIVE_FILTERS":
      const handledFilter = action.payload.filter;
      const newActiveFilters = [...state.activeFilters];
      const handledFilterIndex = newActiveFilters.indexOf(handledFilter);
      if (handledFilterIndex > -1) {
        newActiveFilters.splice(handledFilterIndex, 1);
      } else {
        newActiveFilters.push(handledFilter);
      }

      return {
        ...state,
        activeFilters: newActiveFilters,
      };
    case "CLEAR_ACTIVE_FILTERS":
      return {
        ...state,
        activeFilters: [],
      };
    case "SEARCH_EMPLOYEES":
      return {
        ...state,
        employees: filterEmployeesSearch(state, action),
        options: {
          ...state.options,
          searchString: action.payload.params.searchString,
          searchColumn: action.payload.params.fieldName,
        },
        pagination: {
          ...state.pagination,
        },
      };
    case "SORT_EMPLOYEES":
      return {
        ...state,
        employees: filterEmployeesSort(state, action),
        options: {
          ...state.options,
          sortDirection: action.payload.params.sortDirection,
          sortColumn: action.payload.params.fieldName,
        },
      };
    case "SET_ACTIVITIES":
      return {
        ...state,
        activities: action.payload.activities,
      };
    default:
      return state;
  }
};

const filterEmployeesSearch = (state: any, action: any) => {
  const { fieldName, searchString } = action.payload.params;
  let { employeesPaginated } = state;
  if (!searchString) {
    return [...employeesPaginated];
  }
  return [...employeesPaginated].filter((item: any) => {
    const itemData = item[fieldName];
    const toSearch = Array.isArray(itemData)
      ? itemData.join(", ").toLocaleLowerCase()
      : typeof itemData === "object" &&
        typeof itemData.firstName === "string" &&
        typeof itemData.lastName === "string"
      ? `${itemData.firstName} ${itemData.lastName}`.toLocaleLowerCase()
      : isNumber(itemData) && itemData < 6
      ? itemData
      : moment(itemData).isValid()
      ? formattedTableDateString(itemData).toLocaleLowerCase()
      : itemData;
    return covertGermanLetters(toSearch.toString()).includes(
      covertGermanLetters(searchString.toString()).toLocaleLowerCase()
    );
  });
};

const filterEmployeesSort = (state: any, action: any) => {
  const { fieldName, sortDirection } = action.payload.params;
  let { employeesPaginated } = state;
  if (!sortDirection) {
    return [...employeesPaginated];
  }
  return [...employeesPaginated].sort(
    (a: any, b: any): any => {
      const aField =
        (fieldName === "user"
          ? a[fieldName].firstName + a[fieldName].lastName
          : a[fieldName].toString()) || "";
      const bField =
        (fieldName === "user"
          ? b[fieldName].firstName + b[fieldName].lastName
          : b[fieldName].toString()) || "";
      if (sortDirection === "asc") {
        return aField.toLocaleLowerCase() > bField.toLocaleLowerCase() ? 1 : -1;
      } else if (sortDirection === "dsc") {
        return bField.toLocaleLowerCase() > aField.toLocaleLowerCase() ? 1 : -1;
      }
      return 1;
    }
  );
};

const covertGermanLetters = (text: string) => {
  // Ü, ü     \u00dc, \u00fc
  // Ä, ä     \u00c4, \u00e4
  // Ö, ö     \u00d6, \u00f6
  // ß        \u00df
  return text
    .replace(/\u00e4/g, "ae")
    .replace(/\u00c4/g, "ae")
    .replace(/\u00fc/g, "u")
    .replace(/\u00dc/g, "U")
    .replace(/\u00f6/g, "o")
    .replace(/\u00d6/g, "O");
};

const paginateEmployees = (
  currentPage: any,
  items: any,
  employeesCache: any
) => {
  let totalPages = 1;
  if (!employeesCache.length) {
    return { employees: [], totalPages };
  }
  const total = employeesCache.length;
  totalPages =
    items === "All"
      ? 1
      : parseInt((total / items).toString()) + (total % items === 0 ? 0 : 1);
  items = items === "All" ? total : items;
  const employees = paginate(currentPage, items, employeesCache);
  return { employees, totalPages };
};

const paginate = (currentPage: number, items: number, array: any) => {
  return array.slice((currentPage - 1) * items, currentPage * items);
};

const getFilterRange = (state: any, action: any) => {
  let from;
  let to;
  switch (action.payload.activeFilter) {
    case "currentWeek":
      from = moment()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      to = moment()
        .endOf("isoWeek")
        .format("YYYY-MM-DD");
      break;
    case "lastWeek":
      from = moment()
        .subtract(1, "weeks")
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      to = moment()
        .subtract(1, "weeks")
        .endOf("isoWeek")
        .format("YYYY-MM-DD");
      break;
    case "currentMonth":
      from = moment()
        .startOf("month")
        .format("YYYY-MM-DD");
      to = moment()
        .endOf("month")
        .format("YYYY-MM-DD");
      break;
    case "lastMonth":
      from = moment()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      to = moment()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD");
      break;
    default:
      break;
  }
  return { from, to };
};
